import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { CreateBountyForm, Hackday } from '../types/context';
import MarkdownEditor from './MarkdownEditor';

interface BountyFormProps {
  formData: CreateBountyForm;
  hackdays: Hackday[];
  onChange: (field: keyof CreateBountyForm, value: any) => void;
  onSubmit: () => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitText?: string;
  className?: string;
}

const BountyForm: React.FC<BountyFormProps> = ({
  formData,
  hackdays,
  onChange,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitText = 'Create Bounty',
  className = ''
}) => {
  const [newRequirement, setNewRequirement] = useState('');
  const [newTag, setNewTag] = useState('');

  const addRequirement = () => {
    if (newRequirement.trim()) {
      onChange('requirements', [...formData.requirements, newRequirement.trim()]);
      setNewRequirement('');
    }
  };

  const removeRequirement = (index: number) => {
    onChange('requirements', formData.requirements.filter((_, i) => i !== index));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      onChange('tags', [...formData.tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const removeTag = (index: number) => {
    onChange('tags', formData.tags.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <form onSubmit={handleSubmit} className={`space-y-6 ${className}`}>
      {/* Basic Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Basic Information</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bounty Title *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => onChange('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
              placeholder="Enter bounty title"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <MarkdownEditor
              value={formData.description}
              onChange={(value) => onChange('description', value)}
              placeholder="Describe the bounty task and what needs to be accomplished. Use **bold**, *italic*, [links](url), and - bullet lists."
              height={200}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Associated Hackday *
            </label>
            <select
              required
              value={formData.hackdayId}
              onChange={(e) => onChange('hackdayId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
            >
              <option value="">Select a hackday</option>
              {hackdays.map((hackday) => (
                <option key={hackday.id} value={hackday.id}>
                  {hackday.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status *
            </label>
            <select
              value={formData.status}
              onChange={(e) => onChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Value *
            </label>
            <input
              type="number"
              required
              min="0"
              step="0.01"
              value={formData.value}
              onChange={(e) => onChange('value', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Currency *
            </label>
            <select
              value={formData.currency}
              onChange={(e) => onChange('currency', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="BTC">BTC</option>
              <option value="ETH">ETH</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Winners *
            </label>
            <input
              type="number"
              required
              min="1"
              value={formData.maxWinners}
              onChange={(e) => onChange('maxWinners', parseInt(e.target.value) || 1)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Difficulty
            </label>
            <select
              value={formData.difficulty || ''}
              onChange={(e) => onChange('difficulty', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
            >
              <option value="">Not specified</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
              <option value="expert">Expert</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type *
            </label>
            <select
              value={formData.type}
              onChange={(e) => onChange('type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
            >
              <option value="bug_fix">Bug Fix</option>
              <option value="feature">Feature</option>
              <option value="integration">Integration</option>
              <option value="optimization">Optimization</option>
              <option value="documentation">Documentation</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="datetime-local"
              value={formData.startDate || ''}
              onChange={(e) => onChange('startDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
              style={{ colorScheme: 'light' }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="datetime-local"
              value={formData.endDate || ''}
              onChange={(e) => onChange('endDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
              style={{ colorScheme: 'light' }}
            />
          </div>
        </div>
      </div>

      {/* Requirements */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Requirements</h2>

        <div className="space-y-4">
          <div className="relative">
            <div className="min-h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-accent focus-within:border-transparent flex flex-wrap gap-1.5 items-center">
              {formData.requirements.map((requirement, index) => (
                <span
                  key={index}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${['bg-blue-50 text-blue-700 border-blue-200', 'bg-green-50 text-green-700 border-green-200', 'bg-purple-50 text-purple-700 border-purple-200', 'bg-pink-50 text-pink-700 border-pink-200', 'bg-amber-50 text-amber-700 border-amber-200'][Math.abs(requirement.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % 5]}`}
                >
                  {requirement}
                  <button
                    type="button"
                    onClick={() => removeRequirement(index)}
                    className="ml-1 hover:opacity-70 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={newRequirement}
                onChange={(e) => setNewRequirement(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === ' ' && newRequirement.trim()) {
                    e.preventDefault();
                    addRequirement();
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    if (newRequirement.trim()) {
                      addRequirement();
                    }
                  } else if (e.key === 'Backspace' && !newRequirement && formData.requirements.length > 0) {
                    removeRequirement(formData.requirements.length - 1);
                  }
                }}
                className="flex-1 min-w-[120px] outline-none bg-transparent"
                placeholder={formData.requirements.length === 0 ? "Type and press space to add requirements..." : ""}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Press space or enter to create a requirement. Backspace to remove the last requirement.</p>
          </div>
        </div>
      </div>

      {/* Tags */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Tags</h2>

        <div className="space-y-4">
          <div className="relative">
            <div className="min-h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-accent focus-within:border-transparent flex flex-wrap gap-1.5 items-center">
              {formData.tags.map((tag, index) => (
                <span
                  key={index}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${['bg-blue-50 text-blue-700 border-blue-200', 'bg-green-50 text-green-700 border-green-200', 'bg-purple-50 text-purple-700 border-purple-200', 'bg-pink-50 text-pink-700 border-pink-200', 'bg-amber-50 text-amber-700 border-amber-200'][Math.abs(tag.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % 5]}`}
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(index)}
                    className="ml-1 hover:opacity-70 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === ' ' && newTag.trim()) {
                    e.preventDefault();
                    addTag();
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    if (newTag.trim()) {
                      addTag();
                    }
                  } else if (e.key === 'Backspace' && !newTag && formData.tags.length > 0) {
                    removeTag(formData.tags.length - 1);
                  }
                }}
                className="flex-1 min-w-[120px] outline-none bg-transparent"
                placeholder={formData.tags.length === 0 ? "Type and press space to add tags..." : ""}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Press space or enter to create a tag. Backspace to remove the last tag.</p>
          </div>
        </div>
      </div>

      {/* Sponsor Information (Optional) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Sponsor Information (Optional)</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sponsor Name
            </label>
            <input
              type="text"
              value={formData.sponsorName || ''}
              onChange={(e) => onChange('sponsorName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
              placeholder="Company or organization name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sponsor Website
            </label>
            <input
              type="url"
              value={formData.sponsorWebsite || ''}
              onChange={(e) => onChange('sponsorWebsite', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
              placeholder="https://..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sponsor Logo URL
            </label>
            <input
              type="url"
              value={formData.sponsorLogo || ''}
              onChange={(e) => onChange('sponsorLogo', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
              placeholder="https://... (image URL)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Non-Cash Prizes (Swag)
            </label>
            <input
              type="text"
              value={formData.swag || ''}
              onChange={(e) => onChange('swag', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
              placeholder="e.g., Software licenses, mentorship, swag"
            />
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end space-x-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 bg-[#782c8c] text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <span className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Creating...
            </span>
          ) : (
            submitText
          )}
        </button>
      </div>
    </form>
  );
};

export default BountyForm;
